import { useEffect, useRef, useState } from "react";
import {
  ServiceProvider,
  StageKey,
  STATUS_TYPES,
  StatusKey,
} from "../../../utils/integrationConfigData";

const STAGE_DOTS: StageKey[] = ["crd", "dtr", "pas"];

const resolveStatusKey = (status: string): StatusKey =>
  (status as StatusKey) in STATUS_TYPES ? (status as StatusKey) : "notSet";

interface ProviderListProps {
  providers: ServiceProvider[];
  selectedProviderId?: string | null;
  onSelect?: (provider: ServiceProvider) => void;
  onAddProvider?: () => void;
}

const ProviderList = ({
  providers,
  selectedProviderId,
  onSelect,
  onAddProvider,
}: ProviderListProps) => {
  const itemsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = itemsRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(maxScroll > 1 && el.scrollLeft < maxScroll - 1);
  };

  useEffect(() => {
    const el = itemsRef.current;
    if (!el) return;

    updateScrollState();

    const onScroll = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateScrollState())
        : null;
    resizeObserver?.observe(el);

    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [providers]);

  useEffect(() => {
    const el = itemsRef.current;
    if (!el || !selectedProviderId) return;

    const active = el.querySelector<HTMLElement>(
      ".ic-provider-list-item.active",
    );
    active?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
    window.setTimeout(updateScrollState, 250);
  }, [selectedProviderId]);

  const scrollProviders = (direction: -1 | 1) => {
    const el = itemsRef.current;
    if (!el) return;

    const amount = Math.max(el.clientWidth * 0.75, 140);
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  };

  return (
    <aside className="ic-provider-list" aria-label="Service providers">
      <div className="ic-provider-list-header">
        <div className="ic-provider-list-label">Service Providers</div>
        <button
          type="button"
          className="ic-add-provider-btn"
          onClick={onAddProvider}
        >
          Add Provider
        </button>
      </div>

      <div className="ic-provider-list-scroller">
        <button
          type="button"
          className="ic-provider-list-nav ic-provider-list-nav-prev"
          aria-label="Show previous providers"
          disabled={!canScrollLeft}
          onClick={() => scrollProviders(-1)}
        >
          <i className="fh_arrow_left_line" aria-hidden="true"></i>
        </button>

        <div className="ic-provider-list-items" ref={itemsRef}>
          {providers.map((provider) => {
            const isActive = provider.id === selectedProviderId;

            return (
              <button
                key={provider.id}
                type="button"
                className={`ic-provider-list-item ${isActive ? "active" : ""}`}
                onClick={() => onSelect?.(provider)}
              >
                <div className="ic-provider-list-main">
                  <span className="ic-provider-list-name">
                    {provider.shortName || provider.name}
                  </span>
                  <span className="ic-provider-list-count">
                    {provider.cptCodes}
                  </span>
                </div>
                <div className="ic-provider-list-dots" aria-hidden="true">
                  {STAGE_DOTS.map((stageKey) => {
                    const statusKey = resolveStatusKey(provider[stageKey]);
                    const tone = STATUS_TYPES[statusKey].tone;

                    return (
                      <span
                        key={stageKey}
                        className={`ic-stage-dot ic-stage-dot--${tone} ic-stage-dot--${statusKey}`}
                        title={`${stageKey.toUpperCase()}: ${STATUS_TYPES[statusKey].label}`}
                      />
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="ic-provider-list-nav ic-provider-list-nav-next"
          aria-label="Show next providers"
          disabled={!canScrollRight}
          onClick={() => scrollProviders(1)}
        >
          <i className="fh_arrow_right_line" aria-hidden="true"></i>
        </button>
      </div>
    </aside>
  );
};

export default ProviderList;
